import React, { useState, useCallback, useEffect } from 'react';
import { Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, Divider, Button, FormControl, Select, MenuItem, Backdrop, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Collapse } from '@mui/material';
import { Menu as MenuIcon, Dashboard as DashboardIcon, People as PeopleIcon, LocationOn as LocationIcon, Refresh as RefreshIcon, AutoAwesome as SparkleIcon, Campaign as CampaignIcon, AccessTime as AccessTimeIcon, Map as MapIcon, Send as SendIcon, Assignment as AssignmentIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, ExitToApp as LogoutIcon, Warning as WarningIcon, ExpandMore as ExpandMoreIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import dataSyncService from '../services/dataSyncService';
import indexedDBService from '../services/indexedDBService';
import { useDateRange } from '../context/DateContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const DashboardLayout = ({ onLogout, userRole }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    // Default to collapsed if no saved preference
    return saved !== null ? saved === 'true' : true;
  });
  const { updateDates } = useDateRange();
  const [startDate, setStartDate] = useState(() => {
    const savedStartDate = localStorage.getItem('globalStartDate');
    return savedStartDate ? dayjs(savedStartDate) : dayjs().subtract(30, 'day');
  });
  const [endDate, setEndDate] = useState(() => {
    const savedEndDate = localStorage.getItem('globalEndDate');
    return savedEndDate ? dayjs(savedEndDate) : dayjs();
  });
  const [syncingData, setSyncingData] = useState(() => {
    // Check if there's an ongoing sync, but clear if it's been stuck for more than 5 minutes
    const syncInProgress = localStorage.getItem('syncInProgress');
    const syncStartTime = localStorage.getItem('syncStartTime');
    
    if (syncInProgress === 'true' && syncStartTime) {
      const stuckTime = Date.now() - parseInt(syncStartTime, 10);
      // If sync has been "in progress" for more than 5 minutes, clear it
      if (stuckTime > 5 * 60 * 1000) {
        console.warn('Detected stuck sync, clearing sync state');
        localStorage.removeItem('syncInProgress');
        localStorage.removeItem('syncStartTime');
        return false;
      }
      return true;
    }
    
    // Clear any stale sync state
    if (syncInProgress === 'true' && !syncStartTime) {
      localStorage.removeItem('syncInProgress');
      return false;
    }
    
    return false;
  });
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [nextSyncTime, setNextSyncTime] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auto-expand sections if a child route is active
  const getInitialExpandedSections = () => {
    const path = location.pathname;
    const expanded = {};
    if (path === '/onboarding' || path === '/outreach' || path === '/registrations') {
      expanded['Onboarding'] = true;
    }
    return expanded;
  };
  
  const [expandedSections, setExpandedSections] = useState(getInitialExpandedSections());
  
  // Update expanded sections when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/onboarding' || path === '/outreach' || path === '/registrations') {
      setExpandedSections(prev => ({ ...prev, 'Onboarding': true }));
    }
  }, [location.pathname]);

  // New function to force clear all app data
  const handleForceReset = async () => {
    try {
      setSyncingData(true);
      setCountdown('Resetting app data...');
      
      // Clear sync flags in localStorage
      localStorage.removeItem('syncInProgress');
      localStorage.removeItem('syncStartTime');
      localStorage.removeItem('purelytics_app_version');
      
      // Force refresh IndexedDB
      await indexedDBService.forceRefresh();
      
      // Clear all stores
      await Promise.all([
        indexedDBService.clearClients(),
        indexedDBService.clearLocations(),
        indexedDBService.clearPurePromoteContacts(),
        indexedDBService.clearPurePromoteReports()
      ]);
      
      // Clear localStorage items
      localStorage.removeItem('purePromoteState');
      localStorage.removeItem('purePromoteFilters');
      localStorage.removeItem('purePromoteSelection');
      
      // Set success message
      setCountdown('Reset complete. Reloading...');
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error during force reset:', error);
      setCountdown('Reset failed');
      setSyncingData(false);
    }
  };

  // Add dialog close handler
  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
  };

  // Add dialog confirm handler
  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    handleForceReset();
  };

  const handleRefreshData = useCallback(async () => {
    // Check if sync is already in progress
    const syncInProgress = localStorage.getItem('syncInProgress') === 'true';
    if (syncInProgress || syncingData) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    try {
      // Set sync state with timestamp
      localStorage.setItem('syncInProgress', 'true');
      localStorage.setItem('syncStartTime', Date.now().toString());
      setSyncingData(true);
      setCountdown('Syncing...');
      
      console.log('Starting data refresh...');

      // Clear all IndexedDB stores first
      await Promise.all([
        indexedDBService.clearClients(),
        indexedDBService.clearLocations(),
        indexedDBService.clearPurePromoteContacts(),
        indexedDBService.clearPurePromoteReports()
      ]);

      // Clear any localStorage data related to Pure Promote
      localStorage.removeItem('purePromoteState');
      localStorage.removeItem('purePromoteFilters');
      localStorage.removeItem('purePromoteSelection');

      await dataSyncService.syncData();

      // Update last sync time
      const newLastSync = Date.now();
      setLastSyncTime(new Date(newLastSync));
      setNextSyncTime(new Date(newLastSync + (6 * 60 * 60 * 1000)));

      // Dispatch refresh event
      window.dispatchEvent(new CustomEvent('globalDataRefresh', {
        detail: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      }));

      console.log('Data refresh completed successfully');
      setCountdown('Sync complete');
      setTimeout(() => {
        const diff = nextSyncTime - Date.now();
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown(`${hours}h ${minutes}m ${seconds}s`);
        }
      }, 2000);

    } catch (error) {
      console.error('Error refreshing data:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      setCountdown('Sync failed - ' + (error.message || 'Unknown error'));
      
      // Show error for longer so user can see it
      setTimeout(() => {
        const diff = nextSyncTime ? nextSyncTime - Date.now() : 0;
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown(`${hours}h ${minutes}m ${seconds}s`);
        } else {
          setCountdown('Click REFRESH to retry');
        }
      }, 5000);
    } finally {
      // Always clear sync state, even on error
      console.log('Clearing sync state...');
      localStorage.removeItem('syncInProgress');
      localStorage.removeItem('syncStartTime');
      setSyncingData(false);
    }
  }, [syncingData, startDate, endDate, nextSyncTime]);

  // Update context when local dates change
  useEffect(() => {
    updateDates(startDate, endDate);
  }, [startDate, endDate, updateDates]);

  // Save dates to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('globalStartDate', startDate.toISOString());
    localStorage.setItem('globalEndDate', endDate.toISOString());
  }, [startDate, endDate]);

  // Dispatch initial date change event on mount to notify all components
  useEffect(() => {
    // Dispatch initial date change event so components can initialize with correct dates
    // Use a small timeout to ensure all components have mounted and are listening
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('globalDateChange', {
        detail: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      }));
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Add effect to fetch and update last sync time
  useEffect(() => {
    const fetchLastSyncTime = async () => {
      try {
        const lastSync = await dataSyncService.getLastSyncTime();
        if (lastSync) {
          setLastSyncTime(new Date(lastSync));
          // Calculate next sync time (6 hours from last sync)
          const nextSync = new Date(lastSync + (6 * 60 * 60 * 1000));
          setNextSyncTime(nextSync);
        }
      } catch (error) {
        console.error('Error fetching last sync time:', error);
      }
    };
    fetchLastSyncTime();
  }, []);

  // Add effect for countdown timer and auto-sync
  useEffect(() => {
    if (!nextSyncTime) return;

    const checkAndSync = async () => {
      const now = new Date();
      const diff = nextSyncTime - now;
      
      // Check if sync is already in progress
      const syncInProgress = localStorage.getItem('syncInProgress') === 'true';
      
      if (diff <= 0 && !syncInProgress) {
        // Time to sync
        setCountdown('Syncing...');
        await handleRefreshData();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown(`${hours}h ${minutes}m ${seconds}s`);
    };

    const timer = setInterval(checkAndSync, 1000);
    checkAndSync(); // Initial check

    return () => clearInterval(timer);
  }, [nextSyncTime, handleRefreshData]);

  // Add effect for initial sync check on component mount
  useEffect(() => {
    const checkInitialSync = async () => {
      // Check if there's an ongoing sync
      const syncInProgress = localStorage.getItem('syncInProgress') === 'true';
      const syncStartTime = localStorage.getItem('syncStartTime');
      
      if (syncInProgress && syncStartTime) {
        const stuckTime = Date.now() - parseInt(syncStartTime, 10);
        // If sync has been stuck for more than 5 minutes, clear it and retry
        if (stuckTime > 5 * 60 * 1000) {
          console.warn('Clearing stuck sync on mount');
          localStorage.removeItem('syncInProgress');
          localStorage.removeItem('syncStartTime');
          setSyncingData(false);
        } else {
          setCountdown('Syncing...');
          return;
        }
      } else if (syncInProgress) {
        // Stale sync state without timestamp, clear it
        console.warn('Clearing stale sync state on mount');
        localStorage.removeItem('syncInProgress');
        setSyncingData(false);
      }

      try {
        const lastSync = await dataSyncService.getLastSyncTime();
        if (!lastSync || (Date.now() - lastSync) > 6 * 60 * 60 * 1000) {
          // If no last sync or it's been more than 6 hours
          console.log('Initial sync check: data is stale or missing, starting sync...');
          await handleRefreshData();
        } else {
          console.log('Initial sync check: data is fresh, skipping sync');
        }
      } catch (error) {
        console.error('Error in initial sync check:', error);
        // Don't block the UI if sync check fails
        setSyncingData(false);
        localStorage.removeItem('syncInProgress');
        localStorage.removeItem('syncStartTime');
      }
    };
    checkInitialSync();
  }, [handleRefreshData]);

  // Add effect to clean up sync state if the component unmounts during sync
  useEffect(() => {
    return () => {
      if (syncingData) {
        localStorage.removeItem('syncInProgress');
      }
    };
  }, [syncingData]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  const handleDateChange = useCallback((type, newValue) => {
    if (type === 'start') {
      setStartDate(newValue);
    } else {
      setEndDate(newValue);
    }
    // Update context
    updateDates(
      type === 'start' ? newValue : startDate,
      type === 'end' ? newValue : endDate
    );
    // Save to localStorage
    localStorage.setItem('globalStartDate', type === 'start' ? newValue.toISOString() : startDate.toISOString());
    localStorage.setItem('globalEndDate', type === 'end' ? newValue.toISOString() : endDate.toISOString());
    // Dispatch date change event
    window.dispatchEvent(new CustomEvent('globalDateChange', {
      detail: {
        startDate: type === 'start' ? newValue.toISOString() : startDate.toISOString(),
        endDate: type === 'end' ? newValue.toISOString() : endDate.toISOString()
      }
    }));
  }, [startDate, endDate, updateDates]);

  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    let newStartDate = dayjs();
    const newEndDate = dayjs();

    switch (range) {
      case '30':
        newStartDate = newEndDate.subtract(30, 'day');
        break;
      case '90':
        newStartDate = newEndDate.subtract(90, 'day');
        break;
      case '180':
        newStartDate = newEndDate.subtract(180, 'day');
        break;
      case '365':
        newStartDate = newEndDate.subtract(365, 'day');
        break;
      case 'all':
        newStartDate = dayjs('1970-01-01');
        break;
      default:
        newStartDate = newEndDate.subtract(365, 'day');
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    updateDates(newStartDate, newEndDate);

    // Save to localStorage
    localStorage.setItem('globalStartDate', newStartDate.toISOString());
    localStorage.setItem('globalEndDate', newEndDate.toISOString());

    window.dispatchEvent(new CustomEvent('globalDateChange', {
      detail: {
        startDate: newStartDate.toISOString(),
        endDate: newEndDate.toISOString()
      }
    }));
  };

  const getMenuItems = () => {
    const adminRest = [
      { text: 'Client Watchlist', icon: <WarningIcon />, path: '/client-watchlist' },
      { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
      { text: 'Locations', icon: <LocationIcon />, path: '/locations' },
      { text: 'Pure Promote', icon: <CampaignIcon />, path: '/pure-promote' },
      { text: 'Marketing', icon: <MapIcon />, path: '/marketing' },
      {
        text: 'Onboarding',
        icon: <AssignmentIcon />,
        children: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/onboarding' },
          { text: 'Registrations', icon: <AssignmentIcon />, path: '/registrations' },
          { text: 'Prospecting', icon: <SendIcon />, path: '/outreach' },
        ],
      },
    ];
    if (userRole === 'superadmin') {
      return [
        { text: 'CEO Reporting', icon: <AssessmentIcon />, path: '/ceo' },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        ...adminRest,
      ];
    }
    if (userRole === 'admin') {
      return [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
        ...adminRest,
      ];
    }
    return [
      { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
      { text: 'Locations', icon: <LocationIcon />, path: '/locations' },
      { text: 'Pure Promote', icon: <CampaignIcon />, path: '/pure-promote' },
      {
        text: 'Onboarding',
        icon: <AssignmentIcon />,
        children: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/onboarding' },
          { text: 'Registrations', icon: <AssignmentIcon />, path: '/registrations' },
          { text: 'Prospecting', icon: <SendIcon />, path: '/outreach' },
        ],
      },
    ];
  };

  const menuItems = getMenuItems();

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleMenuToggle = (itemText) => {
    setExpandedSections(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const isItemSelected = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };

  const isChildSelected = (children) => {
    return children.some(child => location.pathname === child.path);
  };

  const isDateRangeNeeded = () => {
    const path = location.pathname;
    if (path === '/ceo') return false;
    return !path.includes('/clients/') && !path.includes('/locations/'); // Disable on detail pages
  };

  const drawer = (
    <div>
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: sidebarCollapsed ? 'center' : 'flex-start', 
        alignItems: 'center',
        minHeight: '64px !important',
        px: sidebarCollapsed ? 1 : 2
      }}>
        {!sidebarCollapsed && (
          <Typography variant="h6" noWrap component="div">
            Purelytics
          </Typography>
        )}
      </Toolbar>
      <Divider />
      {!sidebarCollapsed && (
        <>
          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => handleDateChange('start', newValue)}
                slotProps={{ 
                  textField: { 
                    size: 'small', 
                    sx: { 
                      mb: 2,
                      opacity: isDateRangeNeeded() ? 1 : 0.5,
                      pointerEvents: isDateRangeNeeded() ? 'auto' : 'none'
                    } 
                  } 
                }}
                format="DD/MM/YYYY"
                disabled={!isDateRangeNeeded()}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => handleDateChange('end', newValue)}
                slotProps={{ 
                  textField: { 
                    size: 'small', 
                    sx: { 
                      mb: 2,
                      opacity: isDateRangeNeeded() ? 1 : 0.5,
                      pointerEvents: isDateRangeNeeded() ? 'auto' : 'none'
                    } 
                  } 
                }}
                format="DD/MM/YYYY"
                disabled={!isDateRangeNeeded()}
              />
            </LocalizationProvider>
            
            <FormControl fullWidth size="small" sx={{ 
              mb: 2,
              opacity: isDateRangeNeeded() ? 1 : 0.5,
              pointerEvents: isDateRangeNeeded() ? 'auto' : 'none'
            }}>
              <Select
                defaultValue="30"
                onChange={handleQuickRangeChange}
                disabled={!isDateRangeNeeded()}
              >
                <MenuItem value="30">Last 30 Days</MenuItem>
                <MenuItem value="90">Last 3 Months</MenuItem>
                <MenuItem value="180">Last 6 Months</MenuItem>
                <MenuItem value="365">Last Year</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
           
            <Button
              fullWidth
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshData}
              disabled={syncingData}
              sx={{ mt: 1 }}
            >
              {syncingData ? 'Refreshing...' : 'Refresh All Data'}
            </Button>
          </Box>
          <Divider />
        </>
      )}
      {sidebarCollapsed && (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <IconButton
            onClick={handleRefreshData}
            disabled={syncingData}
            color="primary"
            title="Refresh All Data"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      )}
      <List>
        {menuItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedSections[item.text] || false;
          const isSelected = isItemSelected(item);
          
          if (sidebarCollapsed && hasChildren) {
            // When collapsed, show parent item only with tooltip
            return (
              <ListItem 
                key={item.text}
                sx={{ 
                  justifyContent: 'center',
                  px: 1,
                  py: 0.5,
                }}
                title={item.text}
              >
                <ListItemIcon sx={{ 
                  minWidth: 0,
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
              </ListItem>
            );
          }

          if (hasChildren) {
            // Parent item with children
            return (
              <React.Fragment key={item.text}>
                <ListItem 
                  onClick={() => handleMenuToggle(item.text)}
                  selected={isSelected}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      cursor: 'pointer'
                    },
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    paddingBottom: 1,
                    justifyContent: 'flex-start',
                    px: 2,
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: 56,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItem 
                        key={child.text}
                        onClick={() => handleNavigation(child.path)}
                        selected={location.pathname === child.path}
                        sx={{ 
                          pl: 4,
                          '&:hover': { 
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            cursor: 'pointer'
                          },
                          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                          paddingBottom: 1,
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 40,
                          justifyContent: 'center'
                        }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          // Regular menu item without children
          return (
            <ListItem 
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{ 
                '&:hover': { 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer'
                },
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                paddingBottom: 1,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : 2,
              }}
              title={sidebarCollapsed ? item.text : ''}
            >
              <ListItemIcon sx={{ 
                minWidth: sidebarCollapsed ? 0 : 56,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={item.text} />}
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  // Modify the AppBar section to include the force reset button
  const renderAppBar = () => (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
        ml: { sm: `${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
        transition: 'width 0.3s ease, margin-left 0.3s ease'
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          edge="start"
          onClick={handleSidebarToggle}
          sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          Purelytics Dashboard
        </Typography>
        
        {/* Force Reset Button - Higher z-index to stay above backdrop */}
        <Button 
          color="inherit" 
          size="small"
          variant="outlined"
          onClick={() => {
            // Clear sync state when clicking force reset
            localStorage.removeItem('syncInProgress');
            localStorage.removeItem('syncStartTime');
            setSyncingData(false);
            setResetDialogOpen(true);
          }}
          sx={{ 
            mr: 2, 
            borderColor: 'rgba(255,255,255,0.5)',
            zIndex: (theme) => theme.zIndex.drawer + 10, // Higher than backdrop
            position: 'relative',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Force Reset
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccessTimeIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            {countdown}
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleRefreshData}
            disabled={syncingData}
            title="Refresh Data"
          >
            <RefreshIcon />
          </IconButton>
          
          {/* Last Synced Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <AccessTimeIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Last Synced: {lastSyncTime ? new Date(lastSyncTime).toLocaleString('en-GB', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }) : 'Never'}
            </Typography>
          </Box>
          
          {/* Logout Button */}
          <Button
            color="inherit"
            variant="outlined"
            size="small"
            onClick={onLogout}
            startIcon={<LogoutIcon />}
            sx={{
              ml: 1,
              borderColor: 'rgba(255,255,255,0.5)',
              color: 'rgba(255,255,255,0.9)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            LOGOUT
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );

  // Add the reset confirmation dialog
  const resetDialog = (
    <Dialog
      open={resetDialogOpen}
      onClose={handleResetDialogClose}
      aria-labelledby="reset-dialog-title"
      aria-describedby="reset-dialog-description"
    >
      <DialogTitle id="reset-dialog-title">
        Reset Application Data?
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="reset-dialog-description">
          This will clear all local data and fix sync issues. Use this if the application is stuck loading or syncing.
          You will need to refresh all data afterward.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetDialogClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleResetConfirm} color="error" variant="contained">
          Reset Data
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Function to determine the preset range based on current dates
  const getPresetRange = () => {
    const start = startDate.startOf('day');
    const end = endDate.startOf('day');
    const diff = end.diff(start, 'day');
    
    if (diff <= 30) return '30';
    if (diff <= 90) return '90';
    if (diff <= 180) return '180';
    if (diff <= 365) return '365';
    return 'all';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {renderAppBar()}
      <Box
        component="nav"
        sx={{ 
          width: { sm: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth }, 
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          transition: 'width 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
      
      {/* Add the reset dialog */}
      {resetDialog}
      
      {/* Add loading overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={syncingData}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">
          Syncing Data...
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              console.log('Cancelling sync...');
              localStorage.removeItem('syncInProgress');
              localStorage.removeItem('syncStartTime');
              setSyncingData(false);
              setCountdown('Sync cancelled');
            }}
            sx={{
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Cancel Sync
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              console.log('Force reset from overlay...');
              setSyncingData(false);
              localStorage.removeItem('syncInProgress');
              localStorage.removeItem('syncStartTime');
              handleForceReset();
            }}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 1)'
              }
            }}
          >
            Force Reset
          </Button>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default DashboardLayout; 